/**
 * Family Tree Graph Inspector
 *
 * Low-level utilities for querying the family relationship graph.
 * These functions provide safe, read-only access to the graph structure
 * without modifying any data.
 *
 * All functions are pure and optimized for validation performance.
 */

import type { PersonNodeCanvas, RelationshipEdge } from '@/types/tree-dashboard.types';
import type { PersonRelationshipSummary, FamilyTreeGraphContext } from './types';

// ==================== PERSON EXISTENCE CHECKS ====================

/**
 * Check if a person exists in the graph
 */
export function personExists(context: FamilyTreeGraphContext, personId: string): boolean {
  return context.persons.some(p => p.id === personId);
}

/**
 * Get person by ID (returns null if not found)
 */
export function getPerson(context: FamilyTreeGraphContext, personId: string): PersonNodeCanvas | null {
  return context.persons.find(p => p.id === personId) ?? null;
}

// ==================== RELATIONSHIP EXISTENCE CHECKS ====================

/**
 * Check if a specific relationship already exists between two persons
 */
export function relationshipExists(
  context: FamilyTreeGraphContext,
  personAId: string,
  personBId: string,
  relationshipType: 'parent_child' | 'spouse'
): boolean {
  return context.relationships.some(rel => {
    if (rel.relationship_type !== relationshipType) return false;

    if (relationshipType === 'spouse') {
      // Spouse relationships are bidirectional
      return (
        (rel.person_a_id === personAId && rel.person_b_id === personBId) ||
        (rel.person_a_id === personBId && rel.person_b_id === personAId)
      );
    } else {
      // Parent-child relationships are directional (person_a is parent, person_b is child)
      return rel.person_a_id === personAId && rel.person_b_id === personBId;
    }
  });
}

/**
 * Check if a spouse relationship exists between two persons
 */
export function spouseRelationshipExists(
  context: FamilyTreeGraphContext,
  person1Id: string,
  person2Id: string
): boolean {
  return relationshipExists(context, person1Id, person2Id, 'spouse');
}

/**
 * Check if a parent-child relationship exists
 */
export function parentChildRelationshipExists(
  context: FamilyTreeGraphContext,
  parentId: string,
  childId: string
): boolean {
  return relationshipExists(context, parentId, childId, 'parent_child');
}

// ==================== RELATIONSHIP QUERIES ====================

/**
 * Get all parent IDs for a person
 */
export function getParents(context: FamilyTreeGraphContext, personId: string): string[] {
  return context.relationships
    .filter(rel => rel.relationship_type === 'parent_child' && rel.person_b_id === personId)
    .map(rel => rel.person_a_id);
}

/**
 * Get all child IDs for a person
 */
export function getChildren(context: FamilyTreeGraphContext, personId: string): string[] {
  return context.relationships
    .filter(rel => rel.relationship_type === 'parent_child' && rel.person_a_id === personId)
    .map(rel => rel.person_b_id);
}

/**
 * Get all spouse IDs for a person
 */
export function getSpouses(context: FamilyTreeGraphContext, personId: string): string[] {
  const spouseIds: string[] = [];

  context.relationships
    .filter(rel => rel.relationship_type === 'spouse')
    .forEach(rel => {
      if (rel.person_a_id === personId) {
        spouseIds.push(rel.person_b_id);
      } else if (rel.person_b_id === personId) {
        spouseIds.push(rel.person_a_id);
      }
    });

  return spouseIds;
}

/**
 * Get all sibling IDs for a person (computed from shared parents)
 */
export function getSiblings(context: FamilyTreeGraphContext, personId: string): string[] {
  const parents = getParents(context, personId);

  if (parents.length === 0) return [];

  // Find all children of any of this person's parents
  const siblingSet = new Set<string>();

  parents.forEach(parentId => {
    const children = getChildren(context, parentId);
    children.forEach(childId => {
      if (childId !== personId) {
        siblingSet.add(childId);
      }
    });
  });

  return Array.from(siblingSet);
}

/**
 * Get complete relationship summary for a person
 */
export function getPersonRelationshipSummary(
  context: FamilyTreeGraphContext,
  personId: string
): PersonRelationshipSummary {
  return {
    personId,
    parents: getParents(context, personId),
    children: getChildren(context, personId),
    spouses: getSpouses(context, personId),
    siblings: getSiblings(context, personId),
  };
}

// ==================== ANCESTRY TRAVERSAL ====================

/**
 * Get all ancestor IDs for a person (parents, grandparents, etc.)
 * Uses BFS to traverse up the tree
 */
export function getAncestors(context: FamilyTreeGraphContext, personId: string): Set<string> {
  const ancestors = new Set<string>();
  const visited = new Set<string>();
  const queue: string[] = [personId];

  while (queue.length > 0) {
    const currentId = queue.shift()!;

    if (visited.has(currentId)) continue;
    visited.add(currentId);

    const parents = getParents(context, currentId);

    parents.forEach(parentId => {
      ancestors.add(parentId);
      queue.push(parentId);
    });
  }

  return ancestors;
}

/**
 * Get all descendant IDs for a person (children, grandchildren, etc.)
 * Uses BFS to traverse down the tree
 */
export function getDescendants(context: FamilyTreeGraphContext, personId: string): Set<string> {
  const descendants = new Set<string>();
  const visited = new Set<string>();
  const queue: string[] = [personId];

  while (queue.length > 0) {
    const currentId = queue.shift()!;

    if (visited.has(currentId)) continue;
    visited.add(currentId);

    const children = getChildren(context, currentId);

    children.forEach(childId => {
      descendants.add(childId);
      queue.push(childId);
    });
  }

  return descendants;
}

/**
 * Check if person A is an ancestor of person B
 */
export function isAncestor(
  context: FamilyTreeGraphContext,
  ancestorId: string,
  descendantId: string
): boolean {
  const ancestors = getAncestors(context, descendantId);
  return ancestors.has(ancestorId);
}

/**
 * Check if person A is a descendant of person B
 */
export function isDescendant(
  context: FamilyTreeGraphContext,
  descendantId: string,
  ancestorId: string
): boolean {
  return isAncestor(context, ancestorId, descendantId);
}

// ==================== CYCLE DETECTION ====================

/**
 * Check if adding a parent-child relationship would create a cycle in the graph
 *
 * A cycle occurs when:
 * - The proposed parent is already a descendant of the proposed child
 * - This would create a situation where someone is their own ancestor
 *
 * @param parentId - The proposed parent
 * @param childId - The proposed child
 * @returns true if adding this relationship would create a cycle
 */
export function wouldCreateCycle(
  context: FamilyTreeGraphContext,
  parentId: string,
  childId: string
): boolean {
  // Check if parent is already a descendant of child
  // If so, making parent → child would create a loop
  return isDescendant(context, parentId, childId);
}

// ==================== RELATIONSHIP VALIDATION HELPERS ====================

/**
 * Count current parents for a person
 */
export function countParents(context: FamilyTreeGraphContext, personId: string): number {
  return getParents(context, personId).length;
}

/**
 * Check if person has a parent of a specific gender
 */
export function hasParentOfGender(
  context: FamilyTreeGraphContext,
  personId: string,
  gender: 'male' | 'female' | 'other'
): boolean {
  const parents = getParents(context, personId);

  return parents.some(parentId => {
    const parent = getPerson(context, parentId);
    return parent?.gender === gender;
  });
}

/**
 * Get all relationships for a specific person
 */
export function getPersonRelationships(
  context: FamilyTreeGraphContext,
  personId: string
): RelationshipEdge[] {
  return context.relationships.filter(
    rel => rel.person_a_id === personId || rel.person_b_id === personId
  );
}

/**
 * Check if two persons share any common ancestors
 */
export function shareCommonAncestors(
  context: FamilyTreeGraphContext,
  person1Id: string,
  person2Id: string
): boolean {
  const ancestors1 = getAncestors(context, person1Id);
  const ancestors2 = getAncestors(context, person2Id);

  // Check for intersection
  for (const ancestorId of ancestors1) {
    if (ancestors2.has(ancestorId)) {
      return true;
    }
  }

  return false;
}
